import { canv } from "@/canv/global.js";
import { FONT_REGISTRY } from "@/fonts/constants";
import {
    BULLETED_RADIUS_RATIO,
    HORIZONTAL_PADDING_RATIO,
    LINK_ADJUSTMENT_WIDTH,
    STRIKE_HEIGHT_RATIO,
    TEXT_WIDTH_CACHE_LIMIT,
    UNDERLINE_HEIGHT_RATIO,
    UNDERLINE_VERTICAL_ADJUSTMENT,
} from "../constantsV2";
import type { TextLine, TextSegment, TextV2Data } from "../typesV2";

const GENERIC_FONT_FAMILIES = new Set([
    "serif",
    "sans-serif",
    "monospace",
    "cursive",
    "fantasy",
    "system-ui",
    "ui-serif",
    "ui-sans-serif",
    "ui-monospace",
    "emoji",
    "math",
    "fangsong",
]);

const isGenericOrFamilyList = (fontFamily: string): boolean => {
    return fontFamily.includes(",") || GENERIC_FONT_FAMILIES.has(fontFamily.toLowerCase());
};

const quoteFontFamily = (fontFamily: string): string => {
    const normalized = fontFamily.replace(/['"]/g, "").trim();

    if (isGenericOrFamilyList(normalized)) {
        return normalized;
    }

    return `"${normalized}"`;
};

const hasExactFontVariant = (
    fontFamily: string,
    weight: number,
    style: "normal" | "italic",
): boolean => {
    const normalizedFamily = fontFamily.replace(/['"]/g, "").trim();
    const source = FONT_REGISTRY[normalizedFamily];
    if (!source) return false;

    return source.some((file) => file.weight === weight && file.style === style);
};

export const getSegmentFont = (segment: TextSegment, data: TextV2Data): string => {
    const parts: string[] = [];
    const canUseBold = segment.bold && hasExactFontVariant(data.fontFamily, 700, "normal");
    const canUseItalic = segment.italic && hasExactFontVariant(data.fontFamily, 400, "italic");

    if (canUseBold) {
        parts.push("bold");
    }
    if (canUseItalic) {
        parts.push("italic");
    }
    const fontStyle = parts.length > 0 ? `${parts.join(" ")} ` : "";
    const fontFamily = quoteFontFamily(data.fontFamily);

    return `${fontStyle}${data.fontSize}px ${fontFamily}`;
};

const textWidthCache = new Map<string, number>();

const getTextWidthCacheKey = (text: string, font: string): string => {
    return `${font}::${text}`;
};

const normalizeFontFamily = (fontFamily: string): string => {
    return fontFamily.replace(/['"]/g, "").trim().toLowerCase();
};

const extractFontFamilyFromCacheKey = (cacheKey: string): string | null => {
    const delimiterIndex = cacheKey.indexOf("::");
    if (delimiterIndex === -1) return null;

    const fontPart = cacheKey.slice(0, delimiterIndex);
    const familyMatch = fontPart.match(/\d+(?:\.\d+)?px\s+(.+)$/);
    if (!familyMatch?.[1]) return null;

    return normalizeFontFamily(familyMatch[1]);
};

export const setCanvasFont = (font: string): void => {
    if (canv.ctx.font !== font) {
        canv.ctx.font = font;
    }
};

export const measureTextWidth = (
    text: string,
    font: string,
    assumeFontSet: boolean = false,
): number => {
    const cacheKey = getTextWidthCacheKey(text, font);
    const cachedWidth = textWidthCache.get(cacheKey);
    if (cachedWidth !== undefined) {
        return cachedWidth;
    }

    if (!assumeFontSet) {
        setCanvasFont(font);
    }
    const width = canv.ctx.measureText(text).width;
    textWidthCache.set(cacheKey, width);

    if (textWidthCache.size > TEXT_WIDTH_CACHE_LIMIT) {
        const oldestKey = textWidthCache.keys().next().value;
        if (oldestKey) {
            textWidthCache.delete(oldestKey);
        }
    }

    return width;
};

export const clearTextWidthCache = (fontFamily?: string): void => {
    if (!fontFamily) {
        textWidthCache.clear();
        return;
    }

    const normalizedFamily = normalizeFontFamily(fontFamily);
    for (const key of textWidthCache.keys()) {
        const keyFamily = extractFontFamilyFromCacheKey(key);
        if (keyFamily === normalizedFamily) {
            textWidthCache.delete(key);
        }
    }
};

export const getBaseFont = (data: TextV2Data): string => {
    return `${data.fontSize}px ${quoteFontFamily(data.fontFamily)}`;
};

/**
 * Рассчитывает ширину только текста в строке (без отступов и маркеров).
 */
export const calculateTextWidth = (line: TextLine, data: TextV2Data): number => {
    let textWidth = 0;
    for (const segment of line.segments) {
        textWidth += getSegmentWidth(segment, data);
    }

    return textWidth;
};

export const calculateLinkAdjustment = (line: TextLine): number => {
    let adjustment = 0;
    let prevHadLink = false;

    for (const segment of line.segments) {
        const hasLink = Boolean(segment.link);

        if (hasLink && !prevHadLink) {
            adjustment += LINK_ADJUSTMENT_WIDTH;
        }

        prevHadLink = hasLink;
    }

    return adjustment;
};

export const getSegmentWidth = (segment: TextSegment, data: TextV2Data): number => {
    const font = getSegmentFont(segment, data);
    setCanvasFont(font);
    return measureTextWidth(segment.text, font, true);
};

/**
 * Рассчитывает общую ширину строки, включая текст, отступы и маркеры/номера.
 */
export const calculateLineWidth = (
    line: TextLine,
    data: TextV2Data,
    horizontalPadding: number,
    lineHeight: number,
): number => {
    const textWidth = calculateTextWidth(line, data);
    const adjustedTextWidth = textWidth + calculateLinkAdjustment(line);
    let lineWidth = adjustedTextWidth + 2 * horizontalPadding;

    if (line.listType === "bullet") {
        const bulletedRadius = getListBulletRadius(lineHeight);
        lineWidth += 2 * bulletedRadius + horizontalPadding;
    } else if (line.listType === "ordered") {
        const numberWidth = getListNumberWidth(line.listNumber ?? 1, data);
        lineWidth += numberWidth + horizontalPadding;
    }

    return lineWidth;
};

export const calculateLineWidths = (
    lines: TextLine[],
    data: TextV2Data,
    horizontalPadding: number,
    lineHeight: number,
): number[] => {
    return lines.map((line) => calculateLineWidth(line, data, horizontalPadding, lineHeight));
};

export const getMaxLineWidth = (lineWidths: number[]): number => {
    let maxWidth = 0;
    for (const lineWidth of lineWidths) {
        if (lineWidth > maxWidth) {
            maxWidth = lineWidth;
        }
    }
    return maxWidth;
};

export const getLineStartX = (
    lineWidth: number,
    startX: number,
    textAlign: TextV2Data["textAlign"],
): number => {
    switch (textAlign) {
        case "center":
            return startX - lineWidth / 2;
        case "right":
            return startX - lineWidth;
        case "left":
        default:
            return startX;
    }
};

export const getListBulletX = (
    lineStartX: number,
    horizontalPadding: number,
    lineHeight: number,
): number => {
    const bulletedRadius = getListBulletRadius(lineHeight);

    return lineStartX + horizontalPadding + bulletedRadius;
};

export const getListNumberX = (lineStartX: number, horizontalPadding: number): number => {
    return lineStartX + horizontalPadding;
};

export const getTextStartX = (
    line: TextLine,
    lineStartX: number,
    data: TextV2Data,
    horizontalPadding: number,
    lineHeight: number,
): number => {
    let textStartX = lineStartX + horizontalPadding;

    if (line.listType === "bullet") {
        const bulletedRadius = getListBulletRadius(lineHeight);
        textStartX += 2 * bulletedRadius + horizontalPadding;
    } else if (line.listType === "ordered") {
        const numberWidth = getListNumberWidth(line.listNumber ?? 1, data);
        textStartX += numberWidth + horizontalPadding;
    }

    return textStartX;
};

export const getListNumberWidth = (listNumber: number, data: TextV2Data): number => {
    const numberText = `${listNumber}.`;

    const font = getBaseFont(data);
    setCanvasFont(font);
    return measureTextWidth(numberText, font, true);
};

export const getListBulletRadius = (lineHeight: number): number => {
    return lineHeight * BULLETED_RADIUS_RATIO;
};

/**
 * Обновляет номер для нумерованного списка и возвращает следующий номер.
 * Сбрасывает номер при переходе на маркированный список или обычный текст.
 */
export const updateListNumber = (line: TextLine, currentNumber: number): number => {
    if (line.listType === "ordered") {
        const numberToShow = line.listNumber ?? currentNumber;

        return numberToShow + 1;
    }
    // Сбрасываем при bullet или обычном тексте
    return 1;
};

export const calculateLineHeight = (
    data: TextV2Data,
): {
    lineHeight: number;
    verticalPadding: number;
    horizontalPadding: number;
    symbolWidth: number;
} => {
    const font = `bold ${data.fontSize}px ${quoteFontFamily(data.fontFamily)}`;
    setCanvasFont(font);
    const metrics = canv.ctx.measureText("F");
    const emptyLineHeight = metrics.actualBoundingBoxAscent || data.fontSize;

    const verticalPadding = emptyLineHeight * (2 / 3);
    const horizontalPadding = verticalPadding * HORIZONTAL_PADDING_RATIO;
    const lineHeight = emptyLineHeight + 2 * verticalPadding;

    return { lineHeight, verticalPadding, horizontalPadding, symbolWidth: metrics.width };
};

export const calculateStrikeMetrics = (
    lineHeight: number,
    lineY: number,
): {
    strikeHeight: number;
    strikeY: number;
} => {
    const strikeHeight = lineHeight * STRIKE_HEIGHT_RATIO;
    const strikeY = lineY - lineHeight / 2 - strikeHeight / 2;

    return { strikeHeight, strikeY };
};

export const calculateUnderlineMetrics = (lineHeight: number, lineY: number) => {
    const underlineHeight = lineHeight * UNDERLINE_HEIGHT_RATIO;

    const underlineY = lineY + UNDERLINE_VERTICAL_ADJUSTMENT;

    return { underlineHeight, underlineY };
};
